import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { DatePicker, Spacer } from '@Sage/hoi-poi-ui';

import { getError } from 'utils/Errors';
import { getLiteral } from 'utils/getLiteral';
import { formatDate, getDateFormatPlaceholder } from 'utils/dates';

import './styles/dateRange.scss';

const propTypes = {
    autoFocus: PropTypes.bool,
    error: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    fieldId: PropTypes.string,
    from: PropTypes.string,
    hidden: PropTypes.bool,
    hint: PropTypes.string,
    max: PropTypes.number,
    min: PropTypes.number,
    onChange: PropTypes.func,
    readOnly: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
    shouldRenderField: PropTypes.func,
    syncFromTo: PropTypes.bool,
    to: PropTypes.string,
    value: PropTypes.object,
};

const DateRange = memo(
    ({
        autoFocus,
        error,
        fieldId,
        from = 'from',
        hidden,
        hint,
        max,
        min,
        onChange,
        readOnly,
        shouldRenderField,
        syncFromTo = false,
        to = 'to',
        value = {},
    }) => {
        const lang = useSelector((state) => state.config?.userData?.langISOInterface);
        const userDateFormat = hint || getDateFormatPlaceholder();
        const [fromDate, setFromDate] = useState(value[from] ? new Date(value[from]) : null);
        const [toDate, setToDate] = useState(value[to] ? new Date(value[to]) : null);
        const prevValue = useRef(value);

        // ========================================
        // EFFECTS
        // ========================================
        useEffect(() => {
            if (prevValue.current !== value) {
                setFromDate(value[from] ? new Date(value[from]) : null);
                setToDate(value[to] ? new Date(value[to]) : null);
            }
            prevValue.current = value;
        }, [from, to, value]);

        // ========================================
        // DERIVED STATE & MEMOIZATION
        // ========================================
        const isReadOnly = useCallback(
            (fieldAttr) => {
                if (typeof readOnly === 'object') {
                    return readOnly[fieldAttr];
                }
                return readOnly;
            },
            [readOnly],
        );

        const applyMin = useCallback(
            (date) => {
                if (!min || !date) return date;
                return date < min ? min : date;
            },
            [min],
        );

        const applyMax = useCallback(
            (date) => {
                if (!max || !date) return date;
                return date > max ? max : date;
            },
            [max],
        );

        const fromError = useMemo(() => {
            if (error && typeof error === 'object' && error[from]) {
                return getError(error[from]);
            }
            return null;
        }, [error, from]);

        const toError = useMemo(() => {
            if (error && typeof error === 'object' && error[to]) {
                return getError(error[to]);
            }
            return null;
        }, [error, to]);

        const generalError = typeof error === 'string' ? getError(error) : null;

        // For syncFromTo: when from is set but to is not, start calendar on from date
        const toInitialDate = syncFromTo && fromDate && !toDate ? fromDate : undefined;

        // ========================================
        // EVENT HANDLERS & CALLBACKS
        // ========================================
        const handleFromChange = useCallback(
            (newDate) => {
                const processedDate = applyMin(newDate);
                setFromDate(processedDate);
                onChange?.({
                    [from]: processedDate,
                    [to]: applyMax(toDate),
                });
            },
            [applyMax, applyMin, from, onChange, to, toDate],
        );

        const handleToChange = useCallback(
            (newDate) => {
                const processedDate = applyMax(newDate);
                setToDate(processedDate);
                onChange?.({
                    [from]: applyMin(fromDate),
                    [to]: processedDate,
                });
            },
            [applyMax, applyMin, from, fromDate, onChange, to],
        );

        // ========================================
        // RENDER
        // ========================================
        if (hidden) return null;
        if (shouldRenderField && !shouldRenderField(fieldId)) return null;

        return (
            <div className="fm-field-date-range-v2__inputs">
                <DatePicker
                    autoFocus={autoFocus}
                    calendarButtonLabel={getLiteral('label_today')}
                    error={fromError || (generalError ? true : null)}
                    formatDate={(date) => formatDate(date, 'L')}
                    isClearable
                    isFullWidth
                    isReadOnly={isReadOnly(from)}
                    lang={lang}
                    maxDate={toDate || undefined}
                    onChange={handleFromChange}
                    placeholder={userDateFormat}
                    inputMaskFormat={userDateFormat}
                    value={fromDate}
                />
                <Spacer x={2} />
                <span className="fm-field-date-range-v2__separator">-</span>
                <Spacer x={2} />
                <DatePicker
                    calendarButtonLabel={getLiteral('label_today')}
                    error={toError || (generalError ? true : null)}
                    formatDate={(date) => formatDate(date, 'L')}
                    isClearable
                    isFullWidth
                    isReadOnly={isReadOnly(to)}
                    lang={lang}
                    minDate={fromDate || undefined}
                    onChange={handleToChange}
                    placeholder={userDateFormat}
                    inputMaskFormat={userDateFormat}
                    value={toDate}
                    overrides={{
                        flatpickrOptions: {
                            defaultDate: toInitialDate,
                        },
                    }}
                />
            </div>
        );
    },
);

DateRange.displayName = 'DateRange';
DateRange.propTypes = propTypes;

export default DateRange;
